import { Action, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import {} from '@monorepo/interfaces';
import {
    CsvExportStatus,
    Page,
    SapSearchProgress,
    SapSearchStatus,
    WorkflowData,
    WorkflowDownloadProgress,
    WorkflowDownloadStatus,
    WorkflowSfnStatus,
    WorkflowValidationJob,
    WorkflowValidationStatus
} from '@monorepo/interfaces';
import { EMPTY, filter, Observable, of, Subject, switchMap, takeUntil, takeWhile, tap, throwError, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize, map, take } from 'rxjs/operators';
import { CsvExportStatusDto } from '@monorepo/backend-lib/dtos';
import { DashboardData, WorkflowDataService } from '../../services/workflow-data.service';
import {
    ClearSapSearchProgress,
    ClearSelectedWorkflow,
    ClearValidationJob,
    ClearWorkflowDownload,
    CreateNewWorkflowData,
    DownloadWorkflowCsv,
    CreateNewWorkflowDataFromSapSearch,
    LoadDashboardData,
    LoadImportedWorkflowData,
    LoadMyActiveTasks,
    LoadMyFutureTasks,
    LoadMyPastTasks,
    LoadMyUrgentTasks,
    LoadMyWorkflows,
    LoadSelectedWorkflow,
    PatchWorkflowData,
    PollSapSearchStatus,
    SendWorkflowToNextStep,
    StartValidationJob,
    StartWorkflowDownload,
    UpdateWorkflowData
} from './workflow-data.actions';
import { UserCacheService } from '../../services/user-cache.service';
import { TotalItemCount } from '../../interfaces/workflows/TotalItemCount';
import { WorkflowState } from '../../enums/WorkflowState.enum';
import { WorkflowItemOperations } from '../../components/workflow-item/workflow-item.operations';

export interface WorkflowDataStateModel {
    selectedWorkflow: WorkflowData | undefined;
    workflows: WorkflowData[];
    pastTasks: WorkflowData[];
    activeTasks: WorkflowData[];
    futureTasks: WorkflowData[];
    urgentTasks: WorkflowData[];
    totalItemCount: TotalItemCount;
    isLoading: boolean;
    isCsvExportLoading: boolean;
    sfnStatus: WorkflowSfnStatus | undefined;
    sapSearchProgress: SapSearchProgress | undefined;
    activeRequests: number;
    workflowDownloadProgress: WorkflowDownloadProgress | undefined;
    validationJob: WorkflowValidationJob | undefined;
}

@State<WorkflowDataStateModel>({
    name: 'workflowData',
    defaults: {
        selectedWorkflow: undefined,
        workflows: [],
        pastTasks: [],
        activeTasks: [],
        futureTasks: [],
        urgentTasks: [],
        totalItemCount: { workflows: 0, pastTasks: 0, activeTasks: 0, futureTasks: 0 },
        isLoading: false,
        isCsvExportLoading: false,
        sfnStatus: undefined,
        sapSearchProgress: undefined,
        activeRequests: 0,
        workflowDownloadProgress: undefined,
        validationJob: undefined
    }
})
@Injectable()
export class WorkflowDataState {
    private workflowService = inject(WorkflowDataService);
    private userCacheService = inject(UserCacheService);
    private pollingStop$ = new Subject<void>();
    private sapSearchPollingStop$ = new Subject<void>();
    private validationJobPollingStop$ = new Subject<void>();

    @Selector()
    static selectedWorkflow(state: WorkflowDataStateModel): WorkflowData | undefined {
        return state.selectedWorkflow;
    }

    @Selector()
    static workflows(state: WorkflowDataStateModel): WorkflowData[] {
        return state.workflows;
    }

    @Selector()
    static pastTasks(state: WorkflowDataStateModel): WorkflowData[] {
        return state.pastTasks;
    }
    @Selector()
    static activeTasks(state: WorkflowDataStateModel): WorkflowData[] {
        return state.activeTasks;
    }
    @Selector()
    static futureTasks(state: WorkflowDataStateModel): WorkflowData[] {
        return state.futureTasks;
    }
    @Selector()
    static urgentTasks(state: WorkflowDataStateModel): WorkflowData[] {
        return state.urgentTasks;
    }
    @Selector()
    static totalItemCount(state: WorkflowDataStateModel): TotalItemCount {
        return state.totalItemCount;
    }

    @Selector()
    static isLoading(state: WorkflowDataStateModel): boolean {
        return state.isLoading;
    }

    @Selector()
    static isCsvExportLoading(state: WorkflowDataStateModel): boolean {
        return state.isCsvExportLoading;
    }

    @Selector()
    static sfnStatus(state: WorkflowDataStateModel): WorkflowSfnStatus | undefined {
        return state.sfnStatus;
    }

    @Selector()
    static sapSearchProgress(state: WorkflowDataStateModel): SapSearchProgress | undefined {
        return state.sapSearchProgress;
    }

    @Selector()
    static workflowDownloadProgress(state: WorkflowDataStateModel): WorkflowDownloadProgress | undefined {
        return state.workflowDownloadProgress;
    }

    @Selector()
    static validationJob(state: WorkflowDataStateModel): WorkflowValidationJob | undefined {
        return state.validationJob;
    }

    @Action(LoadSelectedWorkflow)
    loadSelectedWorkflowData(
        ctx: StateContext<WorkflowDataStateModel>,
        action: LoadSelectedWorkflow
    ): Observable<WorkflowData | HttpErrorResponse> {
        this.incrementActiveRequests(ctx);
        return this.workflowService.getWorkflowById(action.workflowId).pipe(
            tap(selectedWorkflow => ctx.patchState({ selectedWorkflow })),
            finalize(() => this.decrementActiveRequests(ctx))
        );
    }

    @Action(LoadMyWorkflows)
    loadAllWorkflows(ctx: StateContext<WorkflowDataStateModel>): Observable<Page<WorkflowData> | HttpErrorResponse> {
        this.incrementActiveRequests(ctx);
        return this.workflowService.getMyWorkflows().pipe(
            take(1),
            switchMap(workflows => {
                if (workflows && Array.isArray(workflows.items)) {
                    const uniqueResponsibleIds = Array.from(new Set(workflows.items.map(w => w.createdBy)));
                    return this.userCacheService.loadUsersData(uniqueResponsibleIds).pipe(map(() => workflows));
                }
                return of(workflows);
            }),
            tap(workflows =>
                ctx.patchState({
                    workflows: workflows.items,
                    totalItemCount: { ...ctx.getState().totalItemCount, workflows: workflows.metadata.itemCount }
                })
            ),
            finalize(() => this.decrementActiveRequests(ctx))
        );
    }

    @Action(LoadMyActiveTasks)
    loadMyActiveTasks(
        ctx: StateContext<WorkflowDataStateModel>,
        action: LoadMyActiveTasks
    ): Observable<Page<WorkflowData> | HttpErrorResponse> {
        this.incrementActiveRequests(ctx);
        return this.workflowService.getMyActiveTasks(action.page, action.pageSize).pipe(
            take(1),
            switchMap(tasks => {
                if (tasks && Array.isArray(tasks.items)) {
                    const uniqueResponsibleIds = Array.from(new Set(tasks.items.map(w => w.createdBy)));
                    return this.userCacheService.loadUsersData(uniqueResponsibleIds).pipe(map(() => tasks));
                }
                return of(tasks);
            }),
            tap((tasks: Page<WorkflowData>) =>
                ctx.patchState({
                    activeTasks: tasks.items,
                    totalItemCount: { ...ctx.getState().totalItemCount, activeTasks: tasks.metadata.itemCount }
                })
            ),
            finalize(() => this.decrementActiveRequests(ctx))
        );
    }

    @Action(LoadMyUrgentTasks)
    loadMyUrgentTasks(ctx: StateContext<WorkflowDataStateModel>): Observable<Page<WorkflowData> | HttpErrorResponse> {
        this.incrementActiveRequests(ctx);
        return this.workflowService.getMyActiveTasks(0, 0).pipe(
            take(1),
            switchMap(tasks => {
                if (tasks && Array.isArray(tasks.items)) {
                    const uniqueResponsibleIds = Array.from(new Set(tasks.items.map(w => w.createdBy)));
                    return this.userCacheService.loadUsersData(uniqueResponsibleIds).pipe(map(() => tasks));
                }
                return of(tasks);
            }),
            tap((tasks: Page<WorkflowData>) =>
                ctx.patchState({
                    urgentTasks: tasks.items.filter(task =>
                        [WorkflowState.DUE_SOON, WorkflowState.OVERDUE].includes(
                            WorkflowItemOperations.getWorkflowState(task) as WorkflowState
                        )
                    )
                })
            ),
            finalize(() => this.decrementActiveRequests(ctx))
        );
    }

    @Action(LoadDashboardData)
    loadDashboardData(ctx: StateContext<WorkflowDataStateModel>): Observable<DashboardData | HttpErrorResponse> {
        this.incrementActiveRequests(ctx);
        return this.workflowService.getMyDashboardData().pipe(
            take(1),
            switchMap(response => {
                const allItems = [
                    ...response.myWorkflows.items,
                    ...response.activeTasks.items,
                    ...response.futureTasks.items,
                    ...response.pastTasks.items,
                    ...response.urgentTasks.items
                ];
                const uniqueResponsibleIds = Array.from(new Set(allItems.map(w => w.createdBy)));
                return this.userCacheService.loadUsersData(uniqueResponsibleIds).pipe(map(() => response));
            }),
            tap(response =>
                ctx.patchState({
                    workflows: response.myWorkflows.items,
                    activeTasks: response.activeTasks.items,
                    futureTasks: response.futureTasks.items,
                    pastTasks: response.pastTasks.items,
                    urgentTasks: response.urgentTasks.items,
                    totalItemCount: {
                        workflows: response.myWorkflows.metadata.itemCount,
                        activeTasks: response.activeTasks.metadata.itemCount,
                        futureTasks: response.futureTasks.metadata.itemCount,
                        pastTasks: response.pastTasks.metadata.itemCount
                    }
                })
            ),
            finalize(() => this.decrementActiveRequests(ctx))
        );
    }

    @Action(LoadMyPastTasks)
    loadMyPastTasks(
        ctx: StateContext<WorkflowDataStateModel>,
        action: LoadMyPastTasks
    ): Observable<Page<WorkflowData> | HttpErrorResponse> {
        this.incrementActiveRequests(ctx);
        return this.workflowService.getMyPastTasks(action.page, action.pageSize).pipe(
            take(1),
            switchMap(tasks => {
                if (tasks && Array.isArray(tasks.items)) {
                    const uniqueResponsibleIds = Array.from(new Set(tasks.items.map(w => w.createdBy)));
                    return this.userCacheService.loadUsersData(uniqueResponsibleIds).pipe(map(() => tasks));
                }
                return of(tasks);
            }),
            tap((tasks: Page<WorkflowData>) =>
                ctx.patchState({
                    pastTasks: tasks.items,
                    totalItemCount: { ...ctx.getState().totalItemCount, pastTasks: tasks.metadata.itemCount }
                })
            ),
            finalize(() => this.decrementActiveRequests(ctx))
        );
    }

    @Action(LoadMyFutureTasks)
    loadMyFutureTasks(
        ctx: StateContext<WorkflowDataStateModel>,
        action: LoadMyFutureTasks
    ): Observable<Page<WorkflowData> | HttpErrorResponse> {
        this.incrementActiveRequests(ctx);
        return this.workflowService.getMyFutureTasks(action.page, action.pageSize).pipe(
            take(1),
            switchMap(tasks => {
                if (tasks && Array.isArray(tasks.items)) {
                    const uniqueResponsibleIds = Array.from(new Set(tasks.items.map(w => w.createdBy)));
                    return this.userCacheService.loadUsersData(uniqueResponsibleIds).pipe(map(() => tasks));
                }
                return of(tasks);
            }),
            tap((tasks: Page<WorkflowData>) =>
                ctx.patchState({
                    futureTasks: tasks.items,
                    totalItemCount: { ...ctx.getState().totalItemCount, futureTasks: tasks.metadata.itemCount }
                })
            ),
            finalize(() => this.decrementActiveRequests(ctx))
        );
    }

    @Action(CreateNewWorkflowData)
    createNewWorkflowData(
        ctx: StateContext<WorkflowDataStateModel>,
        action: CreateNewWorkflowData
    ): Observable<void | HttpErrorResponse> {
        this.incrementActiveRequests(ctx);
        return this.workflowService.createWorkflow(action.workflowData).pipe(
            tap(newWorkflow => {
                const state = ctx.getState();
                ctx.patchState({
                    workflows: [...state.workflows, newWorkflow],
                    selectedWorkflow: newWorkflow
                });
            }),
            switchMap(newWorkflow => this.workflowService.getWorkflowSfnStatus(newWorkflow.id)),
            switchMap(sfnStatus =>
                this.checkStartPollingConditionAndStartPolling(ctx, sfnStatus, ctx.getState().selectedWorkflow?.id)
            )
        );
    }

    @Action(UpdateWorkflowData)
    updateWorkflowData(
        ctx: StateContext<WorkflowDataStateModel>,
        action: UpdateWorkflowData
    ): Observable<WorkflowData | HttpErrorResponse> {
        this.incrementActiveRequests(ctx);
        return this.workflowService.updateWorkflowData(action.workflowData).pipe(
            tap(updatedWorkflow => {
                const state = ctx.getState();
                ctx.patchState({
                    workflows: [...state.workflows, updatedWorkflow],
                    selectedWorkflow: updatedWorkflow
                });
            }),
            finalize(() => this.decrementActiveRequests(ctx))
        );
    }

    @Action(PatchWorkflowData)
    patchWorkflowData(
        ctx: StateContext<WorkflowDataStateModel>,
        action: PatchWorkflowData
    ): Observable<WorkflowData | HttpErrorResponse> {
        this.incrementActiveRequests(ctx);
        return this.workflowService.patchWorkflowData(action.workflowId, action.body).pipe(
            tap(patchedWorkflow => {
                const state = ctx.getState();
                ctx.patchState({
                    workflows: [...state.workflows, patchedWorkflow],
                    selectedWorkflow: patchedWorkflow
                });
            }),
            finalize(() => this.decrementActiveRequests(ctx))
        );
    }

    @Action(SendWorkflowToNextStep)
    sendWorkflowToNextStep(
        ctx: StateContext<WorkflowDataStateModel>,
        action: SendWorkflowToNextStep
    ): Observable<void | HttpErrorResponse> {
        this.incrementActiveRequests(ctx);
        return this.workflowService
            .sendWorkflowToNextStep(action.workflowId, action.stepId, action.bodyWithNextStep)
            .pipe(
                switchMap(() => this.workflowService.getWorkflowSfnStatus(action.workflowId)),
                switchMap(sfnStatus =>
                    this.checkStartPollingConditionAndStartPolling(ctx, sfnStatus, action.workflowId)
                )
            );
    }

    @Action(DownloadWorkflowCsv)
    downloadWorkflowCsv(
        ctx: StateContext<WorkflowDataStateModel>,
        action: DownloadWorkflowCsv
    ): Observable<void | HttpErrorResponse> {
        ctx.patchState({ isCsvExportLoading: true });

        return this.workflowService.triggerCsvExport(action.workflowId).pipe(
            switchMap(() => this.pollCsvExportStatus(action.workflowId)),
            tap(response => {
                if (response.downloadUrl) {
                    window.location.href = response.downloadUrl;
                    return;
                }

                throw new Error(response.error || 'CSV export failed.');
            }),
            map((): void => undefined),
            finalize(() => ctx.patchState({ isCsvExportLoading: false }))
        );
    }

    @Action(LoadImportedWorkflowData)
    loadImportedWorkflowData(
        ctx: StateContext<WorkflowDataStateModel>,
        action: LoadImportedWorkflowData
    ): Observable<WorkflowData | HttpErrorResponse> {
        this.incrementActiveRequests(ctx);
        return this.workflowService.getImportedWorkflowData(action.fileKey).pipe(
            tap(importedWorkflow => {
                const state = ctx.getState();
                ctx.patchState({
                    workflows: [...state.workflows, importedWorkflow],
                    selectedWorkflow: importedWorkflow
                });
            }),
            finalize(() => this.decrementActiveRequests(ctx))
        );
    }

    @Action(ClearSelectedWorkflow)
    clearSelectedWorkflow(ctx: StateContext<WorkflowDataStateModel>): void {
        ctx.patchState({ selectedWorkflow: undefined });
    }

    @Action(CreateNewWorkflowDataFromSapSearch)
    createNewWorkflowDataFromSapSearch(
        ctx: StateContext<WorkflowDataStateModel>,
        action: CreateNewWorkflowDataFromSapSearch
    ): Observable<void | HttpErrorResponse> {
        this.incrementActiveRequests(ctx);
        return this.workflowService.createWorkflowFromSapSearch(action.workflowData).pipe(
            take(1),
            tap(({ processId }) =>
                ctx.patchState({
                    sapSearchProgress: {
                        processId,
                        status: SapSearchStatus.INITIALIZING,
                        itemsFetched: 0,
                        itemsSaved: 0,
                        totalItems: 0,
                        createdAt: Date.now()
                    }
                })
            ),
            switchMap(({ processId }) => this.startSapSearchPolling(ctx, processId)),
            finalize(() => this.decrementActiveRequests(ctx))
        );
    }

    @Action(PollSapSearchStatus)
    pollSapSearchStatus(ctx: StateContext<WorkflowDataStateModel>, action: PollSapSearchStatus): Observable<void> {
        return this.startSapSearchPolling(ctx, action.processId);
    }

    @Action(ClearSapSearchProgress)
    clearSapSearchProgress(ctx: StateContext<WorkflowDataStateModel>): void {
        this.stopSapSearchPolling();
        ctx.patchState({ sapSearchProgress: undefined });
    }

    private startSapSearchPolling(ctx: StateContext<WorkflowDataStateModel>, processId: string): Observable<void> {
        this.stopSapSearchPolling();
        return timer(0, 2000).pipe(
            takeUntil(this.sapSearchPollingStop$),
            switchMap(() => this.workflowService.getSapSearchStatus(processId)),
            switchMap(progress => {
                ctx.patchState({ sapSearchProgress: progress });
                if (progress.status === SapSearchStatus.COMPLETED) {
                    this.stopSapSearchPolling();
                    if (progress.workflowId) {
                        return this.loadSelectedWorkflowData(ctx, new LoadSelectedWorkflow(progress.workflowId)).pipe(
                            map((): void => undefined)
                        );
                    }
                    return EMPTY;
                }
                if (progress.status === SapSearchStatus.ERROR) {
                    this.stopSapSearchPolling();
                    return EMPTY;
                }
                return EMPTY;
            }),
            switchMap(() => EMPTY)
        );
    }

    private stopSapSearchPolling(): void {
        this.sapSearchPollingStop$.next();
    }

    private startSfnStatusPolling(ctx: StateContext<WorkflowDataStateModel>, workflowId: string): Observable<void> {
        // Stop any existing polling
        this.stopPolling();
        return timer(0, 2000).pipe(
            takeUntil(this.pollingStop$),
            switchMap(() => this.workflowService.getWorkflowSfnStatus(workflowId)),
            switchMap(sfnStatus => {
                ctx.patchState({ sfnStatus });
                const currentWorkflow = ctx.getState().selectedWorkflow;
                // Reload workflow data when currentStepId has changed (SFN has advanced)
                if (currentWorkflow && sfnStatus.currentStepId !== currentWorkflow.currentStepId) {
                    this.decrementActiveRequests(ctx);
                    return this.loadSelectedWorkflowData(ctx, new LoadSelectedWorkflow(workflowId));
                }
                return EMPTY;
            }),
            filter(result => result !== undefined),
            take(1),
            map((): void => undefined)
        );
    }

    private checkStartPollingConditionAndStartPolling(
        ctx: StateContext<WorkflowDataStateModel>,
        sfnStatus: WorkflowSfnStatus,
        workflowId: string
    ): Observable<void> {
        ctx.patchState({ sfnStatus });
        // Start polling if we need to track currentStepId changes
        const currentWorkflow = ctx.getState().selectedWorkflow;
        if (currentWorkflow && sfnStatus.currentStepId === currentWorkflow.currentStepId) {
            return this.startSfnStatusPolling(ctx, workflowId);
        }
        return EMPTY;
    }

    private stopPolling(): void {
        this.pollingStop$.next();
    }

    private pollCsvExportStatus(workflowId: string): Observable<CsvExportStatusDto> {
        return timer(0, 2000).pipe(
            switchMap(() => this.workflowService.getCsvExportStatus(workflowId)),
            filter(response => this.isCsvExportFinished(response)),
            take(1)
        );
    }

    private isCsvExportFinished(response: { downloadUrl?: string; status: CsvExportStatus }): boolean {
        if (response.downloadUrl) {
            return true;
        }

        return response.status === CsvExportStatus.COMPLETED || response.status === CsvExportStatus.FAILED;
    }

    @Action(StartWorkflowDownload)
    startWorkflowDownload(
        ctx: StateContext<WorkflowDataStateModel>,
        action: StartWorkflowDownload
    ): Observable<WorkflowDownloadProgress | void> {
        const existing = ctx.getState().workflowDownloadProgress;
        if (existing && !this.isWorkflowDownloadTerminal(existing.status)) {
            return EMPTY;
        }
        ctx.patchState({
            workflowDownloadProgress: {
                processId: '',
                workflowId: action.workflowId,
                status: WorkflowDownloadStatus.INITIALIZING,
                productsWritten: 0,
                totalProducts: 0,
                createdAt: Date.now()
            }
        });

        return this.workflowService.initiateWorkflowDownload(action.workflowId).pipe(
            switchMap(({ processId }) => this.pollWorkflowDownload(ctx, processId)),
            tap(progress => {
                if (progress.status === WorkflowDownloadStatus.COMPLETED && progress.signedUrl) {
                    window.location.href = progress.signedUrl;
                    return;
                }
                if (progress.status === WorkflowDownloadStatus.ERROR) {
                    throw new Error(progress.errorMessage || 'Workflow download failed.');
                }
            }),
            catchError(error => {
                ctx.patchState({ workflowDownloadProgress: undefined });
                return throwError(() => error);
            })
        );
    }

    @Action(ClearWorkflowDownload)
    clearWorkflowDownload(ctx: StateContext<WorkflowDataStateModel>): void {
        ctx.patchState({ workflowDownloadProgress: undefined });
    }

    private pollWorkflowDownload(
        ctx: StateContext<WorkflowDataStateModel>,
        processId: string
    ): Observable<WorkflowDownloadProgress> {
        return timer(0, 2000).pipe(
            switchMap(() => this.workflowService.getWorkflowDownloadProgress(processId)),
            tap(progress => ctx.patchState({ workflowDownloadProgress: progress })),
            takeWhile(progress => !this.isWorkflowDownloadTerminal(progress.status), true)
        );
    }

    private isWorkflowDownloadTerminal(status: WorkflowDownloadStatus): boolean {
        return status === WorkflowDownloadStatus.COMPLETED || status === WorkflowDownloadStatus.ERROR;
    }

    @Action(StartValidationJob)
    startValidationJob(
        ctx: StateContext<WorkflowDataStateModel>,
        action: StartValidationJob
    ): Observable<WorkflowValidationJob> {
        // Cancel any in-flight poll, then seed an INITIALIZING placeholder so
        // the UI can render the progress bar immediately.
        this.validationJobPollingStop$.next();
        ctx.patchState({
            validationJob: {
                jobId: '',
                workflowId: action.workflowId,
                status: WorkflowValidationStatus.INITIALIZING,
                totalProducts: 0,
                processedProducts: 0,
                shards: [],
                createdAt: Date.now()
            }
        });

        return this.workflowService.startWorkflowValidationJob(action.workflowId).pipe(
            switchMap(({ jobId }) => this.pollValidationJob(ctx, jobId)),
            tap(job => {
                if (job.status === WorkflowValidationStatus.FAILED) {
                    throw new Error(job.errorMessage || 'Workflow validation failed.');
                }
            }),
            catchError(error => {
                ctx.patchState({ validationJob: undefined });
                return throwError(() => error);
            })
        );
    }

    @Action(ClearValidationJob)
    clearValidationJob(ctx: StateContext<WorkflowDataStateModel>): void {
        this.validationJobPollingStop$.next();
        ctx.patchState({ validationJob: undefined });
    }

    private pollValidationJob(
        ctx: StateContext<WorkflowDataStateModel>,
        jobId: string
    ): Observable<WorkflowValidationJob> {
        return timer(2000, 2000).pipe(
            switchMap(() => this.workflowService.getWorkflowValidationJob(jobId)),
            tap(job => ctx.patchState({ validationJob: job })),
            takeUntil(this.validationJobPollingStop$),
            takeWhile(job => !this.isValidationJobTerminal(job.status), true)
        );
    }

    private isValidationJobTerminal(status: WorkflowValidationStatus): boolean {
        return status === WorkflowValidationStatus.SUCCEEDED || status === WorkflowValidationStatus.FAILED;
    }

    private incrementActiveRequests(ctx: StateContext<WorkflowDataStateModel>): void {
        const state = ctx.getState();
        const activeRequests = state.activeRequests + 1;
        ctx.patchState({
            activeRequests,
            isLoading: true
        });
    }

    private decrementActiveRequests(ctx: StateContext<WorkflowDataStateModel>): void {
        const state = ctx.getState();
        const activeRequests = state.activeRequests - 1;
        ctx.patchState({
            activeRequests,
            isLoading: activeRequests > 0 // Only set to false, if there are no more open requests
        });
    }
}
